import { useRef, useEffect, useState } from "react";
import dayjs from "dayjs";

import { formatDecimal } from "@aclymatepackages/formatters";
import { findOfficeUtilitiesOnDate } from "@aclymatepackages/calcs/helpers";

import { fetchOurApi } from "../utils/apiCalls";

export const REPORT_CONTENT_HEIGHT = 775;

// Report logos/images are pulled from hosts that don't send CORS headers
// (e.g. im.ages.io), so a plain <img crossOrigin="anonymous"> tag fails to
// load them at all. Fetching server-side and rendering as a base64 data URI
// sidesteps CORS entirely and also keeps html2canvas/react-to-pdf from
// tainting the export canvas.
export const useCorsSafeImageSrc = (imageUrl) => {
  const [imageSrc, setImageSrc] = useState("");

  useEffect(() => {
    if (!imageUrl) {
      return setImageSrc("");
    }

    fetchOurApi({
      path: "/helpers/convert-image",
      method: "POST",
      data: { imageUrl },
      callback: (base64) =>
        setImageSrc(base64 ? `data:image/jpeg;base64, ${base64}` : "")
    });
  }, [imageUrl]);

  return imageSrc;
};

export const useDynamicWrappingPages = (blocks) => {
  const [pageBlocks, setPageBlocks] = useState([]);
  const blocksRef = useRef([]);

  const setRefElement = (element, idx) => {
    blocksRef.current[idx] = element;
  };

  useEffect(() => {
    const buildPageBlocksArray = () => {
      const heights = blocksRef.current.map((ref) => ref?.offsetHeight);
      const heightSum = heights.reduce((sum, height) => sum + height, 0);

      if (heightSum < REPORT_CONTENT_HEIGHT) {
        return setPageBlocks([blocks]);
      }

      let pageIdx = 0;
      let pageBlockHeight = 0;
      let pageBlocks = [[]];

      heights.forEach((height, idx) => {
        if (pageBlockHeight + height < REPORT_CONTENT_HEIGHT) {
          pageBlockHeight += height;
          return pageBlocks[pageIdx].push(blocks[idx]);
        }
        pageBlockHeight = 0;
        pageIdx++;
        pageBlockHeight += height;
        pageBlocks.push([]);
        return pageBlocks[pageIdx].push(blocks[idx]);
      });

      return setPageBlocks(pageBlocks);
    };

    if (blocksRef.current.length > 0 && !pageBlocks.length) {
      buildPageBlocksArray();
    }
  }, [blocks, pageBlocks.length]);

  return { pageBlocks, setRefElement };
};

// Offices created through the app's address picker store one formatted
// `description` and carry no street or city at all, so reading the parts alone
// rendered a Vancouver head office as just "BC". Imported and older records do
// carry the separate parts, so both shapes have to work.
export const formatFacilityAddress = ({
  description,
  street,
  city,
  state,
  zipCode
} = {}) => {
  if (description) {
    return description;
  }

  const addressParts = [street, city, state, zipCode].filter(Boolean);

  return addressParts.length ? addressParts.join(", ") : "Not provided";
};

export const formatUtilitiesInLease = ({
  electricityInLease,
  heatInLease,
  heatIsPurchased
} = {}) => {
  const heatLabel =
    heatIsPurchased === true ? "Heat (purchased heat, Scope 2)" : "Heat";
  const inLease = [
    ...(electricityInLease ? ["Electricity"] : []),
    ...(heatInLease ? [heatLabel] : [])
  ];

  return inLease.length
    ? `${inLease.join(" and ")} in lease`
    : "Directly purchased";
};

// A lease change saved with an effective date lives only in changes[], so
// the base `utilities` can be stale. Describe the lease as it stood at the
// end of the report period (or today when no period is given).
export const buildFacilityBoundaryRow = (office, asOfDate) => {
  const { name, nickname, address, sqFootage, facilityType, type } = office;
  const utilities = findOfficeUtilitiesOnDate(office, asOfDate || new Date());

  return {
    name: name || nickname,
    address: formatFacilityAddress(address),
    facilityType:
      facilityType?.label || facilityType?.value || type || "Not provided",
    squareFootage: sqFootage
      ? formatDecimal(Number(sqFootage))
      : "Not provided",
    utilities: formatUtilitiesInLease(utilities)
  };
};

// Employee home offices are part of the company's footprint, but their addresses are personal data
// and this report is shared with the company's own customers. They're disclosed as an aggregate
// count and total area only. Each employee contributes one area even when several commute schedules
// are on record, so an employee who changed schedules isn't counted twice.
export const buildHomeOfficeAggregateRows = ({
  employees,
  startDate,
  endDate
}) => {
  const periodEmployees = employees.filter(
    ({ startDate: employeeStartDate, endDate: employeeEndDate }) =>
      (employeeStartDate
        ? dayjs(employeeStartDate).isBefore(endDate)
        : true) &&
      (employeeEndDate ? dayjs(employeeEndDate).isAfter(startDate) : true)
  );

  const homeOfficeAreas = periodEmployees
    .map(({ commuteSchedules = [] }) => {
      const { home } =
        commuteSchedules.find(
          ({ home: scheduleHome }) => scheduleHome?.homeOfficeArea
        ) || {};

      return home?.homeOfficeArea;
    })
    .filter(Boolean)
    .map(Number);

  if (!homeOfficeAreas.length) {
    return [];
  }

  const totalHomeOfficeArea = homeOfficeAreas.reduce(
    (sum, area) => sum + area,
    0
  );

  return [
    {
      name: `Employee home offices (${homeOfficeAreas.length})`,
      address: "Aggregated, addresses not disclosed",
      facilityType: "Remote work",
      squareFootage: formatDecimal(totalHomeOfficeArea),
      utilities: "Included in employee residences"
    }
  ];
};

// Composes the facility boundary rows. Kept pure and separate from the hook
// because useWrappedTableRows latches the first non-empty rows array it is
// given and never re-syncs: this is the only table fed by two independently
// loading sources, so handing it a partial array even once permanently freezes
// the table that way. Returning nothing until both sources are ready is the
// whole fix, so it is worth testing directly.
export const buildFacilityBoundaryRows = ({
  offices,
  employees,
  isLoading,
  startDate,
  endDate
}) => {
  // `employees` is undefined until the emissions context populates it, which
  // an empty-array company never is, so this guards the reverse race without
  // hiding the table for a company that genuinely has no employees.
  if (isLoading || !offices || !employees) {
    return [];
  }

  return [
    ...offices.map((office) => buildFacilityBoundaryRow(office, endDate)),
    ...buildHomeOfficeAggregateRows({ employees, startDate, endDate })
  ];
};
